import type { CSSProperties } from 'react'
import type { ThemeTokens } from './theme'

/**
 * Shared inline-style building blocks for the Task 8 pages (Users, Budgets,
 * Keys, Access report), following the same "plain `CSSProperties` object,
 * driven entirely by `ThemeTokens`" pattern already used in
 * `components/AppShell.tsx` — this repo has no CSS framework or
 * styled-components, so pages build styles the same way the shell does.
 */
export function pageStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    page: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.6rem',
      // Full width (Maroun, 24 Sep 2026): a 68rem column left a third of a
      // wide screen empty while the People table wrapped its own cells.
      width: '100%',
      minWidth: 0,
    },
    statsRow: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    section: {
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: '1rem',
      padding: '1.6rem',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '0.75rem',
      marginBottom: '1rem',
    },
    sectionTitle: {
      margin: 0,
      fontSize: '1.05rem',
      fontWeight: 700,
      color: tokens.textPrimary,
    },
    sectionSubtitle: {
      margin: '0.2rem 0 1.1rem',
      fontSize: '0.85rem',
      color: tokens.textSecondary,
      lineHeight: 1.5,
    },
    toolbar: {
      display: 'flex',
      gap: '0.65rem',
      alignItems: 'center',
      marginBottom: '1.1rem',
      flexWrap: 'wrap',
    },
    input: {
      padding: '0.55rem 0.8rem',
      borderRadius: '0.55rem',
      border: `1px solid ${tokens.border}`,
      background: tokens.background,
      color: tokens.textPrimary,
      fontSize: '0.86rem',
      fontFamily: 'inherit',
    },
    textarea: {
      padding: '0.6rem 0.8rem',
      borderRadius: '0.6rem',
      border: `1px solid ${tokens.border}`,
      background: tokens.background,
      color: tokens.textPrimary,
      fontSize: '0.86rem',
      fontFamily: 'inherit',
      resize: 'vertical',
      width: '100%',
    },
    button: {
      padding: '0.55rem 1.1rem',
      borderRadius: '9999px',
      border: 'none',
      background: tokens.brand,
      color: tokens.brandContrast,
      fontWeight: 600,
      fontSize: '0.85rem',
      cursor: 'pointer',
    },
    buttonGhost: {
      padding: '0.5rem 0.9rem',
      borderRadius: '9999px',
      border: `1.5px solid ${tokens.border}`,
      background: 'transparent',
      color: tokens.textPrimary,
      fontWeight: 600,
      fontSize: '0.8rem',
      cursor: 'pointer',
    },
    buttonDanger: {
      padding: '0.5rem 0.9rem',
      borderRadius: '9999px',
      border: 'none',
      background: tokens.danger,
      color: tokens.dangerContrast,
      fontWeight: 600,
      fontSize: '0.8rem',
      cursor: 'pointer',
    },
    error: {
      color: tokens.danger,
      fontSize: '0.85rem',
      margin: '0.3rem 0 0.9rem',
    },
    hint: {
      color: tokens.textSecondary,
      fontSize: '0.8rem',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.15rem 0.55rem',
      borderRadius: '9999px',
      fontSize: '0.72rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
      fontSize: '0.85rem',
      color: tokens.textPrimary,
    },
    checkboxGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '0.5rem 1rem',
      margin: '0.6rem 0 1.1rem',
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(6, 39, 46, 0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      zIndex: 50,
    },
    modalCard: {
      background: tokens.surface,
      borderRadius: '1rem',
      padding: '1.8rem',
      width: '100%',
      maxWidth: '30rem',
      maxHeight: '85vh',
      overflowY: 'auto',
      boxShadow: tokens.shadow,
    },
    modalTitle: {
      margin: '0 0 0.4rem',
      fontSize: '1.1rem',
      fontWeight: 700,
      color: tokens.textPrimary,
    },
    modalBody: {
      fontSize: '0.88rem',
      color: tokens.textSecondary,
      lineHeight: 1.5,
      margin: '0 0 1rem',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.6rem',
      marginTop: '1.3rem',
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.3rem',
      marginBottom: '1rem',
    },
    fieldLabel: {
      fontSize: '0.82rem',
      fontWeight: 600,
      color: tokens.textPrimary,
    },
    callout: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.3rem',
      padding: '0.9rem 1.1rem',
      borderRadius: '0.75rem',
      background: tokens.surfaceRaised,
      border: `1px solid ${tokens.border}`,
      fontSize: '0.84rem',
      color: tokens.textSecondary,
      lineHeight: 1.5,
    },
    actionsCell: {
      display: 'flex',
      gap: '0.4rem',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    pickerWrap: {
      position: 'relative',
    },
    pickerDropdown: {
      position: 'absolute',
      top: 'calc(100% + 0.25rem)',
      left: 0,
      right: 0,
      zIndex: 10,
      maxHeight: '14rem',
      overflowY: 'auto',
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: '0.6rem',
      boxShadow: tokens.shadow,
      padding: '0.3rem',
    },
    pickerOption: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.1rem',
      width: '100%',
      textAlign: 'left',
      padding: '0.45rem 0.6rem',
      borderRadius: '0.45rem',
      border: 'none',
      background: 'transparent',
      color: tokens.textPrimary,
      fontSize: '0.85rem',
      cursor: 'pointer',
    },
    pickerOptionMeta: {
      fontSize: '0.75rem',
      color: tokens.textSecondary,
    },
    chipRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.4rem',
      margin: '0.5rem 0 0',
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.25rem 0.3rem 0.25rem 0.6rem',
      borderRadius: '9999px',
      background: tokens.surfaceRaised,
      border: `1px solid ${tokens.border}`,
      fontSize: '0.8rem',
      color: tokens.textPrimary,
    },
    chipRemove: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.1rem',
      height: '1.1rem',
      borderRadius: '9999px',
      border: 'none',
      background: 'transparent',
      color: tokens.textSecondary,
      cursor: 'pointer',
      fontSize: '0.9rem',
      lineHeight: 1,
    },
    secretBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.6rem 0.8rem',
      borderRadius: '0.6rem',
      border: `1px dashed ${tokens.brand}`,
      background: tokens.surfaceRaised,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: '0.82rem',
      color: tokens.textPrimary,
      overflowX: 'auto',
      wordBreak: 'break-all',
    },
    warningCallout: {
      padding: '0.75rem 0.9rem',
      borderRadius: '0.6rem',
      border: `1px solid ${tokens.danger}`,
      background: tokens.surfaceRaised,
      color: tokens.danger,
      fontSize: '0.82rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    pickerOptionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      width: '100%',
      padding: '0.45rem 0.6rem',
      borderRadius: '0.45rem',
      fontSize: '0.85rem',
      color: tokens.textPrimary,
    },
    pickerOptionRowDisabled: {
      opacity: 0.55,
    },
    pickerTag: {
      marginLeft: 'auto',
      fontSize: '0.72rem',
      color: tokens.textSecondary,
    },
    pickerFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.6rem',
      padding: '0.5rem 0.6rem',
      marginTop: '0.2rem',
      borderTop: `1px solid ${tokens.border}`,
      fontSize: '0.8rem',
      color: tokens.textSecondary,
    },
    linkButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      color: tokens.brandHover,
      fontWeight: 600,
      fontSize: '0.8rem',
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  }
}
